import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, Edit, TrendingDown, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOAN_TYPES = [
  { value: 'home_loan', label: 'Home Loan' },
  { value: 'car_loan', label: 'Car Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'other', label: 'Other' }
];

const LOAN_TYPE_COLORS = {
  home_loan: 'from-blue-400 to-blue-600',
  car_loan: 'from-purple-400 to-purple-600',
  personal_loan: 'from-orange-400 to-orange-600',
  credit_card: 'from-rose-400 to-rose-600',
  other: 'from-gray-400 to-gray-600'
};

export default function Loans({ user, onLogout }) {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'personal_loan',
    original_amount: '',
    current_balance: '',
    interest_rate: '',
    emi_amount: '',
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoans(response.data);
    } catch (error) {
      console.error('Error fetching loans:', error);
      toast.error('Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        original_amount: parseFloat(formData.original_amount),
        current_balance: parseFloat(formData.current_balance),
        interest_rate: parseFloat(formData.interest_rate),
        emi_amount: formData.emi_amount ? parseFloat(formData.emi_amount) : null,
        start_date: new Date(formData.start_date).toISOString()
      };

      if (editingLoan) {
        await axios.put(`${API}/loans/${editingLoan.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Loan updated successfully!');
      } else {
        await axios.post(`${API}/loans`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Loan added successfully!');
      }

      setDialogOpen(false);
      resetForm();
      fetchLoans();
    } catch (error) {
      console.error('Error saving loan:', error);
      toast.error('Failed to save loan');
    }
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/loans/${loanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Loan deleted successfully!');
      fetchLoans();
    } catch (error) {
      console.error('Error deleting loan:', error);
      toast.error('Failed to delete loan');
    }
  };

  const handleEdit = (loan) => {
    setEditingLoan(loan);
    setFormData({
      name: loan.name,
      type: loan.type,
      original_amount: loan.original_amount.toString(),
      current_balance: loan.current_balance.toString(),
      interest_rate: loan.interest_rate.toString(),
      emi_amount: loan.emi_amount ? loan.emi_amount.toString() : '',
      start_date: new Date(loan.start_date).toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'personal_loan',
      original_amount: '',
      current_balance: '',
      interest_rate: '',
      emi_amount: '',
      start_date: new Date().toISOString().split('T')[0]
    });
    setEditingLoan(null);
  };

  const totalLiabilities = loans.reduce((sum, loan) => sum + loan.current_balance, 0);
  const totalOriginal = loans.reduce((sum, loan) => sum + loan.original_amount, 0);
  const totalPaid = totalOriginal - totalLiabilities;

  if (loading) {
    return (
      <div className="min-h-screen p-8">
        <Navigation user={user} onLogout={onLogout} currentPage="/loans" />
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 dark:bg-gray-900">
      <Navigation user={user} onLogout={onLogout} currentPage="/loans" />

      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Debts & Loans</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Track and manage your loans and debts</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700">
                <Plus className="mr-2 h-4 w-4" />
                Add Loan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md dark:bg-gray-800">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">{editingLoan ? 'Edit Loan' : 'Add New Loan'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Loan Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Home Loan - ABC Bank"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Loan Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger className="dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent container={document.body} className="dark:bg-gray-800">
                      {LOAN_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="dark:text-gray-300">Original Loan Amount</Label>
                  <Input
                    type="number"
                    value={formData.original_amount}
                    onChange={(e) => setFormData({ ...formData, original_amount: e.target.value })}
                    placeholder="0"
                    step="0.01"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Current Outstanding Balance</Label>
                  <Input
                    type="number"
                    value={formData.current_balance}
                    onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                    placeholder="0"
                    step="0.01"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Interest Rate (%)</Label>
                  <Input
                    type="number"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    placeholder="e.g., 8.5"
                    step="0.01"
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Monthly EMI (Optional)</Label>
                  <Input
                    type="number"
                    value={formData.emi_amount}
                    onChange={(e) => setFormData({ ...formData, emi_amount: e.target.value })}
                    placeholder="0"
                    step="0.01"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Loan Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <Button type="submit" className="w-full">
                  {editingLoan ? 'Update Loan' : 'Add Loan'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900 dark:to-pink-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Liabilities</p>
                <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                  {formatCurrency(totalLiabilities, user?.currency)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center">
                <TrendingDown className="text-white" size={24} />
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900 dark:to-teal-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Paid Off</p>
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  {formatCurrency(totalPaid, user?.currency)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <TrendingDown className="text-white rotate-180" size={24} />
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Loans</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{loans.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                <AlertCircle className="text-white" size={24} />
              </div>
            </div>
          </Card>
        </div>

        {/* Loans List */}
        {loans.length === 0 ? (
          <Card className="p-12 text-center dark:bg-gray-800">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 dark:text-gray-300 text-lg">No loans tracked yet</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Add your first loan to start tracking liabilities</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {loans.map((loan) => {
              const paidPercentage = ((loan.original_amount - loan.current_balance) / loan.original_amount) * 100;
              const loanType = LOAN_TYPES.find(t => t.value === loan.type);
              
              return (
                <Card key={loan.id} className="p-6 hover:shadow-lg transition-shadow dark:bg-gray-800">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${LOAN_TYPE_COLORS[loan.type]} flex items-center justify-center flex-shrink-0`}>
                        <TrendingDown className="text-white" size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100">{loan.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{loanType?.label || loan.type}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(loan)}
                        className="dark:text-blue-400 dark:hover:bg-blue-950"
                      >
                        <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(loan.id)}
                        className="dark:text-rose-400 dark:hover:bg-rose-950"
                      >
                        <Trash2 className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Outstanding Balance</span>
                      <span className="font-semibold text-rose-600 dark:text-rose-400">
                        {formatCurrency(loan.current_balance, user?.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Original Amount</span>
                      <span className="text-gray-800 dark:text-gray-200">
                        {formatCurrency(loan.original_amount, user?.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Interest Rate</span>
                      <span className="text-gray-800 dark:text-gray-200">{loan.interest_rate}% p.a.</span>
                    </div>
                    {loan.emi_amount && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Monthly EMI</span>
                        <span className="text-gray-800 dark:text-gray-200">
                          {formatCurrency(loan.emi_amount, user?.currency)}
                        </span>
                      </div>
                    )}
                    <div className="pt-2">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600 dark:text-gray-400">Paid Off</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{paidPercentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-emerald-400 to-teal-500 h-2 rounded-full transition-all"
                          style={{ width: `${paidPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
